import { IconUser, Spot, type SpotProps } from '@n26/nxd'

/**
 * Avatar that renders a person's initials inside a coloured `Spot`, with a
 * deterministic colour derived from the name. Falls back to a user icon when no
 * usable initials can be derived.
 */

const COLORS = ['petrol', 'rhubarb', 'teal', 'wheat'] as const

// Apostrophes/backticks split names (e.g. O'Brien -> "OB").
const APOSTROPHES = /['’`]/g
const LETTER = /\p{L}/u

export const generateColor = (text: string): SpotProps['color'] => {
  const sum = text
    .split('')
    .reduce((total, char) => total + char.charCodeAt(0), 0)
  return COLORS[sum % COLORS.length] ?? 'neutral'
}

export const generateInitials = (fullName: string): string =>
  fullName
    .replace(APOSTROPHES, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .filter((_, index, parts) => index === 0 || index === parts.length - 1)
    // First code point of each part; keep only letters (drops emoji/digits).
    .map(part => [...part][0] ?? '')
    .filter(char => LETTER.test(char))
    .join('')
    .toUpperCase()

type ProfileInitialsProps = {
  label: string
  size?: SpotProps['size']
}

export const ProfileInitials = ({
  label,
  size = 'standard',
}: ProfileInitialsProps) => {
  const initials = generateInitials(label)
  return (
    <Spot color={generateColor(label)} size={size}>
      {initials || <IconUser />}
    </Spot>
  )
}
